"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, Switch, Chip, Avatar, Card, CardBody } from "@nextui-org/react";
import { Upload, MapPin, Search, X, Wand2, Utensils, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BasicUserType } from "@/types/user";
import { createMeal } from "@/actions/meals/mutations";
import { apiClient } from "@/lib/api-client";

interface CreateMealModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: BasicUserType | null;
}

interface NutritionalInfo {
  calories: string;
  servingSize: string;
  protein: string;
  carbs: {
    total: string;
    fiber: string;
    sugar: string;
  };
  fats: {
    total: string;
    saturated: string;
  };
  vitamins: string[];
  minerals: string[];
}

export function CreateMealModal({ isOpen, onClose, user }: CreateMealModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [image, setImage] = useState<string>();
  const [overlayImage, setOverlayImage] = useState<string>();
  const [postCaption, setPostCaption] = useState<string>("");
  const [showPreview, setShowPreview] = useState(false);
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [currentIngredient, setCurrentIngredient] = useState("");
  const [brands, setBrands] = useState<string[]>([]);
  const [currentBrand, setCurrentBrand] = useState("");
  const [taggedUsers, setTaggedUsers] = useState<BasicUserType[]>([]);
  const [searchUsers, setSearchUsers] = useState("");
  const [mealName, setMealName] = useState("");
  const [description, setDescription] = useState("");
  const [cuisineType, setCuisineType] = useState("");
  const [allergens, setAllergens] = useState<string[]>([]);
  const [dietaryCategories, setDietaryCategories] = useState<string[]>([]);
  const [nutritionalInfo, setNutritionalInfo] = useState<NutritionalInfo>({
    calories: "",
    servingSize: "",
    protein: "",
    carbs: {
      total: "",
      fiber: "",
      sugar: ""
    },
    fats: {
      total: "",
      saturated: ""
    },
    vitamins: [],
    minerals: []
  });

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      if (!file?.type?.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        console.log("Image size (bytes):", result.length);
        setImage(result);
      };
      reader.onerror = () => {
        console.error("FileReader error:", reader.error);
        toast.error("Failed to read image file");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAIAnalysis = async () => {
    if (!image) {
      toast.error("Please upload an image first");
      return;
    }

    try {
      setIsAnalyzing(true);
      console.log("Starting AI analysis...");
      
      // Log image data size and validate format
      console.log("Image data size:", image?.length || 0);
      if (!image?.startsWith('data:image')) {
        toast.error("Invalid image format. Please upload a valid image.");
        return;
      }

      toast.loading("Analyzing your meal...");
      
      const responseData = await apiClient.analyzeMeal({
        image,
        createPostWithResults: false
      });

      console.log("Successfully parsed analysis response:", responseData);
      toast.dismiss();
      
      // Get the analysis data
      const analysis = responseData.analysis;
      
      // Update form with AI analysis
      setMealName(analysis?.food_detection?.main_dish || "");
      setCuisineType(analysis?.food_detection?.cuisine_type || "");
      setDescription(
        `${analysis?.food_detection?.presentation || ""}. ${analysis?.food_detection?.preparation_method || ""}. Health Rating: ${analysis?.dietary_notes?.health_rating || ""}`
      );

      // Update ingredients
      const allIngredients = [
        ...(analysis?.ingredients?.main_ingredients || []),
        ...(analysis?.ingredients?.garnishes || []),
        ...(analysis?.ingredients?.sauces || []),
      ].filter(Boolean);
      setIngredients(allIngredients);

      // Update nutritional info
      setNutritionalInfo({
        calories: analysis?.nutritional_analysis?.estimated_calories || "",
        servingSize: analysis?.nutritional_analysis?.serving_size || "",
        protein: analysis?.nutritional_analysis?.macronutrients.protein || "",
        carbs: {
          total: analysis?.nutritional_analysis?.macronutrients.carbohydrates.total || "",
          fiber: analysis?.nutritional_analysis?.macronutrients.carbohydrates.fiber || "",
          sugar: analysis?.nutritional_analysis?.macronutrients.carbohydrates.sugar || ""
        },
        fats: {
          total: analysis?.nutritional_analysis?.macronutrients.fats.total || "",
          saturated: analysis?.nutritional_analysis?.macronutrients.fats.saturated || ""
        },
        vitamins: analysis?.nutritional_analysis?.micronutrients.vitamins || [],
        minerals: analysis?.nutritional_analysis?.micronutrients.minerals || []
      });

      // Update dietary info
      setAllergens(analysis?.dietary_notes?.allergens || []);
      setDietaryCategories(analysis?.dietary_notes?.dietary_category || []);

      // Generate a preview caption for the post
      const caption = `I just analyzed my ${analysis?.food_detection?.main_dish}!\n\nCalories: ${analysis?.nutritional_analysis?.estimated_calories}\nProtein: ${analysis?.nutritional_analysis?.macronutrients.protein}\nCarbs: ${analysis?.nutritional_analysis?.macronutrients.carbohydrates.total}\nFats: ${analysis?.nutritional_analysis?.macronutrients.fats.total}`;
      setPostCaption(caption);

      // Now get the overlay image
      toast.loading("Generating image preview with overlay...");
      
      // Get the overlay image
      const overlayResponse = await apiClient.getMealOverlay({
        image,
        analysis
      });

      setOverlayImage(overlayResponse?.overlay?.overlayImageUrl);
      
      // Show the preview
      setShowPreview(true);
      toast.dismiss();
      toast.success("AI analysis complete! Review and post your meal.");
    } catch (error) {
      console.error("AI Analysis error:", error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : "Failed to analyze image");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const addIngredient = () => {
    if (currentIngredient.trim()) {
      setIngredients([...ingredients, currentIngredient.trim()]);
      setCurrentIngredient("");
    }
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const addBrand = () => {
    if (currentBrand.trim()) {
      setBrands([...brands, currentBrand.trim()]);
      setCurrentBrand("");
    }
  };

  const removeBrand = (index: number) => {
    setBrands(brands.filter((_, i) => i !== index));
  };

  const handleUserSearch = async () => {
    try {
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/users/search?q=${searchUsers}`);
      if (!response.ok) throw new Error("Failed to search users");
      const users = await response.json();
      // Show users in a dropdown or similar UI component
    } catch (error) {
      console.error(error);
      toast.error("Failed to search users");
    }
  };

  const addTaggedUser = (user: BasicUserType) => {
    if (!taggedUsers.find(u => u.id === user.id)) {
      setTaggedUsers([...taggedUsers, user]);
    }
  };

  const removeTaggedUser = (userId: string) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  // Helper function to reset all state
  const resetState = () => {
    setImage(undefined);
    setOverlayImage(undefined);
    setShowPreview(false);
    setPostCaption("");
    setMealName("");
    setDescription("");
    setCuisineType("");
    setIngredients([]);
    setAllergens([]);
    setDietaryCategories([]);
    setCurrentIngredient("");
    setBrands([]);
    setCurrentBrand("");
    setTaggedUsers([]);
    setSearchUsers("");
    setNutritionalInfo({
      calories: "",
      servingSize: "",
      protein: "",
      carbs: {
        total: "",
        fiber: "",
        sugar: ""
      },
      fats: {
        total: "",
        saturated: ""
      },
      vitamins: [],
      minerals: []
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      
      if (!image) {
        toast.error("Please upload an image");
        return;
      }
      
      // If we have a preview with overlay, use that for posting
      if (showPreview && overlayImage) {
        toast.loading("Posting your meal with overlay...");
        
        // Use the nutritionalInfo state which was populated from the analysis
        const mealNutritionalInfo = {
          calories: nutritionalInfo.calories || "500",
          protein: nutritionalInfo.protein || "20g",
          carbs: nutritionalInfo?.carbs?.total || "30g",
          fats: nutritionalInfo?.fats?.total || "10g",
          qualityScore: "85"
        };
        
        // Log the nutritional values
        console.log("Nutritional values from state:", mealNutritionalInfo);

        // Create a post with the overlay image and caption
        const response = await apiClient.createPost({
          content: postCaption || `I just analyzed my ${mealName}!\n\nCalories: ${mealNutritionalInfo.calories}\nProtein: ${mealNutritionalInfo.protein}\nCarbs: ${mealNutritionalInfo.carbs}\nFats: ${mealNutritionalInfo.fats}`,
          type: "meal",
          meal: {
            name: mealName,
            description: description,
            ingredients,
            brands,
            isOrganic: e?.currentTarget?.isOrganic?.checked || false,
            hasSeedOils: e?.currentTarget?.hasSeedOils?.checked || false,
            nutritionalInfo: mealNutritionalInfo
          },
          medias: [
            {
              url: overlayImage,
              type: "image"
            }
          ]
        } as any);
        
        const post = response.post;
        
        // Create the meal data
        const mealResponse = await apiClient.createMeal({
          postId: post.id.toString(),
          name: mealName,
          description: description,
          ingredients,
          brands,
          isOrganic: e?.currentTarget?.isOrganic?.checked || false,
          hasSeedOils: e?.currentTarget?.hasSeedOils?.checked || false,
          taggedUsers: Array.isArray(taggedUsers) ? taggedUsers?.map(u => u.id) : [],
          cuisineType,
          allergens,
          dietaryCategories,
          nutritionalInfo: {
            calories: nutritionalInfo.calories || "500",
            servingSize: nutritionalInfo.servingSize || "1 serving",
            protein: nutritionalInfo.protein || "20g",
            carbs: {
              total: nutritionalInfo?.carbs?.total || "30g",
              fiber: nutritionalInfo?.carbs?.fiber || "5g",
              sugar: nutritionalInfo?.carbs?.sugar || "10g"
            },
            fats: {
              total: nutritionalInfo?.fats?.total || "10g",
              saturated: nutritionalInfo?.fats?.saturated || "3g"
            },
            vitamins: nutritionalInfo.vitamins || [],
            minerals: nutritionalInfo.minerals || []
          }
        });
        
        toast.dismiss();
        toast.success("Meal posted successfully to your feed with nutritional overlay!");
        onClose();
        return;
      }
      
      // If no preview, use the original flow
      toast.loading("Creating your meal post...");
      
      // Use the analyze endpoint with createPostWithResults option
      const response = await apiClient.analyzeMeal({
        image,
        createPostWithResults: true
      });
      
      const result = response;
      toast.dismiss();
      
      if (result.analysis && result?.analysis?.post && result?.analysis?.post.id) {
        console.log("Post created successfully with ID:", result?.analysis?.post.id);
        toast.success("Meal posted successfully to your feed with nutritional overlay!");
        onClose();
      } else {
        console.log("No post ID returned, falling back to createMeal function");
        // Fall back to the original form submission if direct posting fails
        const formData = new FormData(e.currentTarget);
        
        const mealData = {
          name: mealName || formData.get("name") as string,
          description: description || formData.get("description") as string,
          ingredients,
          brands,
          isOrganic: formData.get("isOrganic") === "true",
          hasSeedOils: formData.get("hasSeedOils") === "true",
          taggedUsers: Array.isArray(taggedUsers) ? taggedUsers?.map(u => u.id) : [],
          locationName: formData.get("location") as string,
          image,
          cuisineType,
          nutritionalInfo,
          allergens,
          dietaryCategories,
          user
        };

        console.log("Creating meal with data:", JSON.stringify(mealData, null, 2));
        await createMeal(mealData);
        toast.success("Meal created successfully!");
        onClose();
      }
    } catch (error) {
      console.error("Error creating meal:", error);
      toast.error(error instanceof Error ? error.message : "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      className="bg-secondary max-h-[90vh]"
      scrollBehavior="inside"
      closeButton
      classNames={{
        base: "relative",
        closeButton: [
          "right-4",
          "top-4",
          "text-white",
          "hover:bg-white/20",
          "z-50", // Ensure button is clickable above other content
          "text-lg" // Increase click area
        ]
      }}
    >
      <ModalContent>
        <ModalHeader className="flex justify-between items-center border-b sticky top-0 z-10">
          <h2 className="text-xl">Create New Meal</h2>
          <div className="flex gap-2 mr-12">
            <Button
              variant="light"
              onPress={onClose}
              isDisabled={isLoading || isAnalyzing}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              form="meal-form"
              isDisabled={isLoading || isAnalyzing || !image}
            >
              {isLoading ? "Creating..." : "Post with Analysis"}
            </Button>
          </div>
        </ModalHeader>
        <ModalBody className="overflow-y-auto">
          <form id="meal-form" onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Image Upload Section */}
              <div>
                <p className="text-sm mb-2">Meal Image</p>
                <div className="flex items-center gap-x-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="image-upload"
                    variant="light"
                    className="cursor-pointer"
                    startContent={<Upload className="h-4 w-4" />}
                  >
                    Upload Image
                  </Button>
                  <Button
                    isDisabled={!image || isAnalyzing}
                    onClick={handleAIAnalysis}
                    color="secondary"
                    startContent={<Wand2 className="h-4 w-4" />}
                  >
                    {isAnalyzing ? "Analyzing..." : "Analyze with AI"}
                  </Button>
                  <Button
                    variant="light"
                    onClick={resetState}
                    isDisabled={isLoading || isAnalyzing || !image}
                    startContent={<X className="h-4 w-4" />}
                  >
                    Clear
                  </Button>
                  <Button
                    color="secondary"
                    variant="light"
                    isDisabled={isLoading || isAnalyzing || !image}
                  >
                    Log Privately
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleAIAnalysis}
                    isDisabled={!image || isAnalyzing}
                    startContent={<Wand2 className="h-4 w-4" />}
                  >
                    {isAnalyzing ? "Analyzing..." : "Analyze Meal"}
                  </Button>
                  {image && !overlayImage && (
                    <img src={image} alt="Preview" className="h-20 w-20 object-cover rounded-md" />
                  )}
                </div>
              </div>

              {/* Preview Section */}
              {showPreview && overlayImage && (
                <Card className="w-full">
                  <CardBody className="space-y-4">
                    <h3 className="text-lg font-semibold">Preview Post</h3>
                    
                    <div className="flex flex-col items-center">
                      <img 
                        src={overlayImage} 
                        alt="Preview with overlay" 
                        className="max-w-full rounded-lg shadow-lg mb-4" 
                      />
                      
                      <Textarea
                        label="Post Caption"
                        value={postCaption}
                        onChange={(e) => setPostCaption(e?.target?.value)}
                        variant="bordered"
                        minRows={4}
                        fullWidth
                        className="w-full"
                      />
                      
                      <div className="flex gap-2 mt-4">
                        <Button
                          color="primary"
                          type="submit"
                          isDisabled={isLoading}
                        >
                          {isLoading ? "Posting..." : "Post to Feed"}
                        </Button>
                        <Button
                          variant="light"
                          onClick={() => {
                            setShowPreview(false);
                            setOverlayImage(undefined);
                          }}
                        >
                          Edit Details
                        </Button>
                        <Button
                          color="danger"
                          variant="light"
                          onClick={resetState}
                          startContent={<X className="h-4 w-4" />}
                        >
                          Start Over
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Hide all form fields when preview is shown */}
              {!showPreview && (
                <>
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      id="name"
                      name="name"
                      label="Meal Name"
                      value={mealName}
                      onChange={(e) => setMealName(e?.target?.value)}
                      variant="bordered"
                      classNames={{
                        input: "bg-transparent"
                      }}
                      required
                    />

                    <Input
                      id="cuisine"
                      name="cuisine"
                      label="Cuisine Type"
                      value={cuisineType}
                      onChange={(e) => setCuisineType(e?.target?.value)}
                      variant="bordered"
                      startContent={<Utensils className="h-4 w-4" />}
                      classNames={{
                        input: "bg-transparent"
                      }}
                    />
                  </div>

                  <Textarea
                    id="description"
                    name="description"
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e?.target?.value)}
                    variant="bordered"
                    classNames={{
                      input: "bg-transparent"
                    }}
                    required
                  />

                  {/* Nutritional Information */}
                  <Card>
                    <CardBody>
                      <h3 className="text-lg font-semibold mb-4">Nutritional Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Calories"
                          value={nutritionalInfo.calories}
                          onChange={(e) => setNutritionalInfo({...nutritionalInfo, calories: e?.target?.value})}
                          variant="bordered"
                        />
                        <Input
                          label="Serving Size"
                          value={nutritionalInfo.servingSize}
                          onChange={(e) => setNutritionalInfo({...nutritionalInfo, servingSize: e?.target?.value})}
                          variant="bordered"
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <Input
                          label="Protein (g)"
                          value={nutritionalInfo.protein}
                          onChange={(e) => setNutritionalInfo({...nutritionalInfo, protein: e?.target?.value})}
                          variant="bordered"
                        />
                        <Input
                          label="Total Carbs (g)"
                          value={nutritionalInfo?.carbs?.total}
                          onChange={(e) => setNutritionalInfo({
                            ...nutritionalInfo,
                            carbs: {...nutritionalInfo.carbs, total: e?.target?.value}
                          })}
                          variant="bordered"
                        />
                        <Input
                          label="Total Fat (g)"
                          value={nutritionalInfo?.fats?.total}
                          onChange={(e) => setNutritionalInfo({
                            ...nutritionalInfo,
                            fats: {...nutritionalInfo.fats, total: e?.target?.value}
                          })}
                          variant="bordered"
                        />
                      </div>
                    </CardBody>
                  </Card>

                  {/* Ingredients */}
                  <div>
                    <p className="text-sm mb-2">Ingredients</p>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={currentIngredient}
                        onChange={(e) => setCurrentIngredient(e?.target?.value)}
                        placeholder="Add ingredient"
                        variant="bordered"
                        classNames={{
                          input: "bg-transparent"
                        }}
                      />
                      <Button onClick={addIngredient}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ingredients?.map((ingredient, index) => (
                        <Chip
                          key={index}
                          onClose={() => removeIngredient(index)}
                          variant="flat"
                        >
                          {ingredient}
                        </Chip>
                      ))}
                    </div>
                  </div>

                  {/* Dietary Information */}
                  <Card>
                    <CardBody>
                      <h3 className="text-lg font-semibold mb-4">Dietary Information</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm mb-2">Allergens</p>
                          <div className="flex flex-wrap gap-2">
                            {allergens?.map((allergen, index) => (
                              <Chip
                                key={index}
                                color="warning"
                                variant="flat"
                                startContent={<AlertTriangle className="h-3 w-3" />}
                              >
                                {allergen}
                              </Chip>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm mb-2">Dietary Categories</p>
                          <div className="flex flex-wrap gap-2">
                            {dietaryCategories?.map((category, index) => (
                              <Chip key={index} color="success" variant="flat">
                                {category}
                              </Chip>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Brands/Sourcing */}
                  <div>
                    <p className="text-sm mb-2">Brands/Sourcing</p>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={currentBrand}
                        onChange={(e) => setCurrentBrand(e?.target?.value)}
                        placeholder="Add brand"
                        variant="bordered"
                        classNames={{
                          input: "bg-transparent"
                        }}
                      />
                      <Button onClick={addBrand}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {brands?.map((brand, index) => (
                        <Chip
                          key={index}
                          onClose={() => removeBrand(index)}
                          variant="flat"
                        >
                          {brand}
                        </Chip>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <Switch name="isOrganic" defaultSelected={false}>
                      Organic
                    </Switch>
                    <Switch name="hasSeedOils" defaultSelected={false}>
                      Contains Seed Oils
                    </Switch>
                  </div>

                  <Input
                    id="location"
                    name="location"
                    label="Location"
                    variant="bordered"
                    startContent={<MapPin className="h-4 w-4" />}
                    classNames={{
                      input: "bg-transparent"
                    }}
                  />

                  {/* Tag Users */}
                  <div>
                    <p className="text-sm mb-2">Tag Users</p>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={searchUsers}
                        onChange={(e) => setSearchUsers(e?.target?.value)}
                        placeholder="Search users"
                        variant="bordered"
                        startContent={<Search className="h-4 w-4" />}
                        classNames={{
                          input: "bg-transparent"
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {taggedUsers?.map((user) => (
                        <Chip
                          key={user.id}
                          onClose={() => removeTaggedUser(user.id)}
                          variant="flat"
                          avatar={<Avatar src={user.image || ""} name={user.name} />}
                        >
                          {user.name}
                        </Chip>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CreateMealModal;